import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Plus, Trash2, Shield, UserCheck, Eye, EyeOff, Users, Key, AlertTriangle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`
});

const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', className: 'bg-red-50 text-red-700 border-red-200' },
  admin: { label: 'Admin', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  staff: { label: 'Staff', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  attendee: { label: 'Attendee', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const TeamManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('admins');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPassword, setBulkPassword] = useState('');
  const [bulkResult, setBulkResult] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [adminsRes, usersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/retreat/admins`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/retreat/users/all`, { headers: getAuthHeaders() }),
      ]);
      setAdmins(adminsRes.data);
      setAllUsers(usersRes.data);
    } catch {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddAdmin = async () => {
    if (!form.email || !form.password) {
      toast.error('Email and password are required');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/admins`, form, { headers: getAuthHeaders() });
      if (res.data.upgraded_existing) {
        toast.success(`${form.email} already had an attendee login — it's been upgraded to ${form.role} with the new password. They can use it for both the admin panel and their portal.`, { duration: 8000 });
      } else {
        toast.success(`${form.role === 'admin' ? 'Admin' : 'Staff'} account created`);
      }
      setShowModal(false);
      setForm({ name: '', email: '', password: '', role: 'admin' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAdmin = async (admin) => {
    if (admin.is_default) {
      toast.error('Cannot delete the default admin');
      return;
    }
    if (!window.confirm(`Remove ${admin.email} as ${admin.role}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/retreat/admins/${admin.id}`, { headers: getAuthHeaders() });
      toast.success('Admin removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const handleResetAdminPassword = async (admin) => {
    const pw = window.prompt(`New password for ${admin.email} (min 6 characters):`);
    if (pw === null) return;
    if (pw.trim().length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await axios.put(`${BACKEND_URL}/api/retreat/admins/${admin.id}/password`, { password: pw.trim() }, { headers: getAuthHeaders() });
      toast.success(`Password updated for ${admin.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const getRoleBadge = (role) => {
    const config = ROLE_CONFIG[role] || { label: role, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={`${config.className} border`}>{config.label}</Badge>;
  };

  const handleBulkSetup = async () => {
    if (!bulkPassword || bulkPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/retreat/users/bulk-setup`,
        { password: bulkPassword, overwrite_existing: true, skip_placeholder_emails: true },
        { headers: getAuthHeaders() }
      );
      setBulkResult(res.data);
      toast.success(`${res.data.created} accounts created, ${res.data.updated} updated`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk setup failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const attendeeUsers = allUsers.filter(u => u.role === 'attendee');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="team-management-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team &amp; Users</h1>
          <p className="text-gray-600 mt-1">Manage admin accounts and view all registered users</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => { setBulkResult(null); setBulkPassword(''); setShowBulkModal(true); }}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="bulk-setup-btn"
          >
            <Users className="w-4 h-4 mr-2" />Bulk Setup Accounts
          </Button>
          <Button
            onClick={() => setShowModal(true)}
            className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
            data-testid="add-admin-btn"
          >
            <Plus className="w-4 h-4 mr-2" />Add Admin
          </Button>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('admins')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors
            ${tab === 'admins' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-admins"
        >
          <Shield className="w-4 h-4" />Admins ({admins.length})
        </button>
        <button
          onClick={() => setTab('users')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors
            ${tab === 'users' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-users"
        >
          <UserCheck className="w-4 h-4" />Attendee Accounts ({attendeeUsers.length})
        </button>
      </div>

      {tab === 'admins' ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Admin &amp; Staff Accounts</CardTitle>
          </CardHeader>
          <CardContent>
            {admins.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No admins found</p>
            ) : (
              <div className="space-y-3" data-testid="admins-list">
                {admins.map((admin) => (
                  <div
                    key={admin.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    data-testid={`admin-row-${admin.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-semibold text-sm">
                        {(admin.name || admin.email)[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900">
                            {admin.name || admin.email}
                          </p>
                          {getRoleBadge(admin.role)}
                          {admin.is_default && (
                            <Badge className="bg-gray-100 text-gray-500 border-gray-200 border text-xs">Default</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{admin.email}</p>
                      </div>
                    </div>
                    {!admin.is_default && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResetAdminPassword(admin)}
                          className="text-gray-500 hover:text-gray-800"
                          title="Reset password"
                          data-testid={`reset-admin-password-${admin.id}`}
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAdmin(admin)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-admin-${admin.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Attendee Accounts</CardTitle>
          </CardHeader>
          <CardContent>
            {attendeeUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="font-medium">No attendee accounts yet</p>
                <p className="text-sm mt-1">Attendees create accounts when they accept their invite</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="attendee-users-list">
                {attendeeUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-sm">
                        {user.attendee_name ? user.attendee_name[0] : user.email[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {user.attendee_name || user.email}
                        </p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    {getRoleBadge(user.role)}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Admin Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent data-testid="add-admin-modal">
          <DialogHeader>
            <DialogTitle>Add Admin</DialogTitle>
            <DialogDescription>Create a new admin or staff account. They can log in at /admin/login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                data-testid="admin-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="admin@example.com"
                data-testid="admin-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="At least 6 characters"
                  className="pr-10"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="admin-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — Full access</SelectItem>
                  <SelectItem value="staff">Staff — Limited access</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button
              onClick={handleAddAdmin}
              disabled={saving}
              className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
              data-testid="save-admin-btn"
            >
              {saving ? 'Creating...' : 'Create Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Setup Accounts Modal */}
      <Dialog open={showBulkModal} onOpenChange={(open) => { if (!open) { setShowBulkModal(false); setBulkResult(null); } }}>
        <DialogContent data-testid="bulk-setup-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Bulk Setup Attendee Accounts
            </DialogTitle>
            <DialogDescription>
              Create login accounts for every attendee with a shared password. Existing accounts will have their passwords updated.
            </DialogDescription>
          </DialogHeader>

          {!bulkResult ? (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-900 leading-relaxed">
                  <strong>Heads up:</strong> Every attendee will use the same password to sign in. Use this for fast onboarding before the retreat — consider sending unique invite links for production-grade security.
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">Shared password</Label>
                <Input
                  type="text"
                  value={bulkPassword}
                  onChange={(e) => setBulkPassword(e.target.value)}
                  placeholder="e.g. MN-2Ae$w9"
                  className="font-mono"
                  data-testid="bulk-password-input"
                />
                <p className="text-xs text-gray-500">Minimum 6 characters. Share this with attendees through your communication channel.</p>
              </div>

              <div className="text-xs text-gray-500 space-y-1 bg-gray-50 rounded-lg p-3">
                <p><strong>What this does:</strong></p>
                <ul className="list-disc list-inside ml-1 space-y-0.5">
                  <li>Creates a login for every attendee with a real email</li>
                  <li>Updates existing accounts to use this password</li>
                  <li>Skips placeholder emails (e.g. "@example.com")</li>
                  <li>Each attendee logs in at <code className="bg-white px-1 rounded">/login</code> with their email + this password</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                <p className="text-sm font-semibold text-emerald-900 mb-2">Setup complete</p>
                <ul className="text-xs text-emerald-800 space-y-0.5">
                  <li>✓ Created: <strong>{bulkResult.created}</strong> new accounts</li>
                  <li>✓ Updated: <strong>{bulkResult.updated}</strong> existing accounts</li>
                  {bulkResult.skipped_placeholder > 0 && (
                    <li>· Skipped placeholder emails: {bulkResult.skipped_placeholder}</li>
                  )}
                  {bulkResult.skipped_no_email > 0 && (
                    <li>· Skipped (no email): {bulkResult.skipped_no_email}</li>
                  )}
                  <li className="pt-1 mt-1 border-t border-emerald-200">
                    Total attendees: {bulkResult.total_attendees}
                  </li>
                </ul>
              </div>
              {bulkResult.errors && bulkResult.errors.length > 0 && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-800">
                  <p className="font-semibold mb-1">Some errors occurred:</p>
                  <ul className="space-y-0.5">
                    {bulkResult.errors.slice(0, 5).map((e, i) => <li key={i}>· {e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!bulkResult ? (
              <>
                <Button variant="outline" onClick={() => setShowBulkModal(false)}>Cancel</Button>
                <Button
                  onClick={handleBulkSetup}
                  disabled={bulkBusy || !bulkPassword}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  data-testid="bulk-setup-confirm-btn"
                >
                  {bulkBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                  {bulkBusy ? 'Setting up...' : 'Create All Accounts'}
                </Button>
              </>
            ) : (
              <Button onClick={() => setShowBulkModal(false)} className="bg-gray-900 hover:bg-gray-800 text-white">
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
